import { ACQUIRED_STATUS } from '@travel/traveler-core/constants/salesPromotion';
import { isNotEmptyArray } from '@travel/utils';

import type { AcquiredCoupon } from 'AcquiredCoupons-Types';
import type { ItemPlan, PricingDetail } from 'BookingStep-Types';
import {
  INCENTIVE_CALCULATION_METHOD,
  INCENTIVE_METHOD,
  OFFER_TYPE,
} from 'constants/salesPromotion';
import type { SalesPromotion } from 'ProviderList-Types';
import type { CouponDetails } from 'SalesPromotion-Types';

/**
 * Checks if the discount price should be displayed
 * @param salesPromotions salesPromotion array
 * @returns true is discount price should be displayed, false if not
 */
export const shouldDisplayDiscountPrice = (salesPromotions: SalesPromotion[]) => {
  /*
   * If there is change in price, discount should be shown
   * In detail should meet all conditions:
   * - incentive is discount
   * - direct offer or acquired coupon
   */
  return salesPromotions?.some(
    (sp) =>
      sp.incentive.method === INCENTIVE_METHOD.DISCOUNT &&
      (sp.offerType === OFFER_TYPE.DIRECT ||
        sp.acquiredStatus === ACQUIRED_STATUS.ACQUIRED_BY_USER),
  );
};

/**
 * Get all the applied sales promotion ids
 * @param salesPromotions sales promotion array
 * @returns array of ids
 */
export const getAppliedSalesPromotionIds = (salesPromotions: SalesPromotion[]) => {
  return salesPromotions
    ?.filter(
      (sp) =>
        sp.acquiredStatus === ACQUIRED_STATUS.APPLIED_AUTOMATICALLY ||
        sp.acquiredStatus === ACQUIRED_STATUS.ACQUIRED_BY_USER,
    )
    .map((sp) => sp.id);
};

/**
 * Judges whether to show disclaimer for local amount
 * @param coupon coupon details information
 * @param currency user currency
 * @returns true if should show disclaimer
 */
export const shouldDisplayCouponDisclaimer = (
  coupon?: CouponDetails | AcquiredCoupon,
  currency?: string,
) => {
  return (
    coupon?.localCurrency !== currency &&
    (coupon?.incentiveAmount?.incentiveCalcMethod === INCENTIVE_CALCULATION_METHOD.EXACT_AMOUNT ||
      (coupon?.incentiveAmount?.incentiveCalcMethod === INCENTIVE_CALCULATION_METHOD.PERCENT &&
        coupon?.incentiveAmount?.limit))
  );
};

/**
 * Get promotion details for both DIRECT and INDIRECT providers
 * @param itemPlans Array of item plans containing applied promotion IDs
 * @param pricingDetail Pricing detail containing sales promotions and points
 * @param roomIndex Index of the room (defaults to 0 for INDIRECT providers)
 * @returns Object containing applied promotions and their characteristics
 */
export const getPromotionDetails = (
  itemPlans: Array<ItemPlan> | undefined,
  pricingDetail: PricingDetail | undefined,
  roomIndex: number = 0,
) => {
  /* itemPlans form field is available in BS 1. Hence, we use the appliedSalesPromotionId and appliedSubSalesPromotionId
   * fields to find the currently selected promotion.
   * itemPlans form field is not available in BS 2. Hence, we pick the used promotion's details from the pricingDetail array. */

  let usedMainPromotion = null;
  let usedSubPromotion = null;
  let mainSalesPromotionId = '';
  let subSalesPromotionId = '';

  const hasItemPlans = isNotEmptyArray(itemPlans);
  if (hasItemPlans) {
    mainSalesPromotionId = itemPlans[roomIndex]?.appliedSalesPromotionId || '';
    subSalesPromotionId = itemPlans[roomIndex]?.appliedSubSalesPromotionId || '';
  }

  // Early return if pricingDetail is not available
  if (!pricingDetail) {
    return {
      appliedSalesPromotion: null,
      appliedSubSalesPromotion: null,
      isMainPoint: false,
      isSubPoint: false,
      hasMoneyPromotion: false,
      hasTotalPointsUsed: false,
    };
  }

  const { totalPointsUsed, salesPromotions } = pricingDetail;

  usedMainPromotion = salesPromotions.find((promo) => promo.selected);
  usedSubPromotion =
    usedMainPromotion &&
    usedMainPromotion.subPromotions &&
    usedMainPromotion?.subPromotions.find((promo) => promo.selected);

  if (mainSalesPromotionId) {
    // find usedPromotion details from pricingDetail.salesPromotion
    usedMainPromotion = salesPromotions.find(
      (promotion) => promotion.detail.id === mainSalesPromotionId,
    );
    if (subSalesPromotionId) {
      usedSubPromotion =
        usedMainPromotion &&
        usedMainPromotion.subPromotions &&
        usedMainPromotion?.subPromotions.find((p) => p.detail.id === subSalesPromotionId);
    }
  }

  const isMainPoint = usedMainPromotion?.detail.incentiveMethod === INCENTIVE_METHOD.POINT;
  const isSubPoint = usedSubPromotion?.detail.incentiveMethod === INCENTIVE_METHOD.POINT;

  return {
    appliedSalesPromotion: usedMainPromotion,
    appliedSubSalesPromotion: usedSubPromotion,
    isMainPoint,
    isSubPoint,
    hasMoneyPromotion: Boolean(
      (usedMainPromotion && !isMainPoint) || (usedSubPromotion && !isSubPoint),
    ),
    hasTotalPointsUsed: Boolean(totalPointsUsed),
  };
};

export const isRspDirectOffer = (
  salesPromotions: Array<{ offerType?: OFFER_TYPE; promotionType?: string }> | undefined,
): boolean =>
  salesPromotions?.some(
    (promotion) =>
      promotion?.offerType === OFFER_TYPE.DIRECT && promotion?.promotionType === 'RAKUTEN',
  ) ?? false;

export const getIsRspDirectOfferApplicable = (
  pricingDetails: PricingDetail[] | undefined,
): boolean =>
  pricingDetails?.some((pricingDetail) =>
    pricingDetail?.salesPromotions?.some(
      (promotion) =>
        promotion?.detail?.offerType === OFFER_TYPE.DIRECT &&
        promotion?.promotionType === 'RAKUTEN' &&
        promotion?.selected,
    ),
  ) ?? false;
